import { clinicalLimitStatus } from '@/clients/details/v2/components/analysis/clinicalTargetSemantics';
import { suggestedRdaToneStyles } from '@/clients/details/v2/components/analysis/suggestedRdaTone';
import { format, isValid, parse } from 'date-fns';
import type { DrvPrimaryType } from '@/lib/api/drv';
import type { ContributorRow, DayAnalysisSnapshot } from '@/components/meal-planning/manual/ManualMealPlanAnalysisDashboard';
import {
  findDrvRequirementByNutrientIds,
  resolveDrvPrimaryTypeValueOnly,
  resolveDrvRecommendedValue,
} from '@/utils/drv-resolve-primary-target';
import { roundNutritionDisplay } from '@/utils/nutrition-display-format';
import type {
  ExpandedFoodRow,
  FoodSearchResult,
  IngredientServingOverrideOption,
  MacroTotals,
  RecipeServingSizeOption,
} from './nutritionalAnalysisTypes';
import {
  MANUAL_ANALYSIS_MEAL_COLORS,
  MEAL_TIME_REF_DATE,
  defaultMealTimesByName,
} from './nutritionalAnalysisConstants';
import { formatUnderscoreDisplayLabel } from '@/utils/display-labels';
import { getNutritionTrafficLightColor } from '@/utils/nutrition-traffic-light';

const toOptionalNumber = (value: unknown): number | undefined => {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeUnitOptions = (rawUnits: unknown): string[] => {
  const values = Array.isArray(rawUnits) ? rawUnits : [rawUnits];
  const seen = new Set<string>();
  const normalized: string[] = [];
  values.forEach((rawUnit) => {
    const unit = String(rawUnit ?? '').trim();
    if (!unit.length) return;
    const key = unit.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    normalized.push(unit);
  });
  return normalized;
};

const normalizeStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const normalized: string[] = [];
  value.forEach((entry) => {
    const raw = String(entry ?? '').trim();
    const key = raw.toLowerCase();
    if (!raw || seen.has(key)) return;
    seen.add(key);
    normalized.push(raw);
  });
  return normalized;
};

const normalizeIngredientServingOverrides = (rawServingOverrides: unknown): IngredientServingOverrideOption[] => {
  if (!Array.isArray(rawServingOverrides)) return [];
  const seen = new Set<string>();
  const normalized: IngredientServingOverrideOption[] = [];
  rawServingOverrides.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const servingId = String((entry as { servingId?: unknown }).servingId ?? '').trim();
    if (!servingId.length) return;
    const key = servingId.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    const gramsRaw = Number((entry as { grams?: unknown }).grams);
    normalized.push({
      servingId,
      grams: Number.isFinite(gramsRaw) ? gramsRaw : undefined,
    });
  });
  return normalized;
};

const resolveIngredientServingMetadataFromRecord = (
  record: Record<string, unknown>,
): {
  possibleUnits: string[];
  servingOverrides?: IngredientServingOverrideOption[];
  defaultServingId?: string;
  defaultServingGrams?: number;
} => {
  const ingredientRecord =
    record.ingredient && typeof record.ingredient === 'object'
      ? (record.ingredient as Record<string, unknown>)
      : undefined;

  const rootOverrides = normalizeIngredientServingOverrides(record.servingOverrides);
  const ingredientOverrides = normalizeIngredientServingOverrides(ingredientRecord?.servingOverrides);
  const mergedOverrides = normalizeIngredientServingOverrides([...rootOverrides, ...ingredientOverrides]);

  const defaultServingId = normalizeUnitOptions([
    record.defaultServingId,
    ingredientRecord?.defaultServingId,
  ])[0];

  const defaultServingGramsRaw = Number(record.defaultServingGrams ?? ingredientRecord?.defaultServingGrams);
  const fallbackDefaultGrams =
    defaultServingId && mergedOverrides.length > 0
      ? mergedOverrides.find((option) => option.servingId.toLowerCase() === defaultServingId.toLowerCase())?.grams
      : undefined;
  const defaultServingGrams = Number.isFinite(defaultServingGramsRaw)
    ? defaultServingGramsRaw
    : fallbackDefaultGrams;

  return {
    possibleUnits: normalizeUnitOptions([
      ...normalizeUnitOptions(record.possibleUnits),
      ...normalizeUnitOptions(ingredientRecord?.possibleUnits),
      ...mergedOverrides.map((option) => option.servingId),
      ...normalizeUnitOptions([defaultServingId]),
    ]),
    ...(mergedOverrides.length > 0 ? { servingOverrides: mergedOverrides } : {}),
    ...(defaultServingId ? { defaultServingId } : {}),
    ...(defaultServingGrams != null ? { defaultServingGrams } : {}),
  };
};

const resolveIngredientUnitOptionsFromRecord = (record: Record<string, unknown>): string[] => {
  return resolveIngredientServingMetadataFromRecord(record).possibleUnits;
};

const mapFoodSearchResults = (
  items: Array<{
    id: string;
    name: string;
    description?: unknown;
    type: 'recipe' | 'ingredient';
    source: 'cached' | 'spoonacular';
    provider?: 'internal' | 'spoonacular' | 'custom';
    sourceId?: string;
    allergens?: unknown;
    allergenTagIds?: unknown;
    dietaryTagIds?: unknown;
    dietLabels?: unknown;
    intoleranceTagIds?: unknown;
    intoleranceTags?: unknown;
    tagClassificationStatus?: unknown;
    tagClassificationSource?: unknown;
    tagClassificationUpdatedAt?: unknown;
    isVegan?: unknown;
    isVegetarian?: unknown;
    isGlutenFree?: unknown;
    isDairyFree?: unknown;
    possibleUnits?: unknown;
    servingOverrides?: unknown;
    defaultServingId?: unknown;
    defaultServingGrams?: unknown;
    clinicalWarnings?: unknown;
    ingredient?: {
      possibleUnits?: unknown;
      servingOverrides?: unknown;
      defaultServingId?: unknown;
      defaultServingGrams?: unknown;
    } | null;
    nutrition?: {
      calories?: unknown;
      protein?: unknown;
      carbohydrate?: unknown;
      total_fat?: unknown;
    } | null;
  }> | undefined,
): FoodSearchResult[] =>
  (items ?? [])
    .filter((item) =>
      (item.type === 'recipe' || item.type === 'ingredient')
      && (item.source === 'cached' || item.source === 'spoonacular')
      && typeof item.id === 'string'
      && typeof item.name === 'string'
      && item.name.trim().length > 0,
    )
    .map((item) => {
      const nutrition = item.nutrition && typeof item.nutrition === 'object' ? item.nutrition : undefined;
      const itemRecord = item as Record<string, unknown>;
      const ingredientServingMetadata =
        item.type === 'ingredient'
          ? resolveIngredientServingMetadataFromRecord(itemRecord)
          : undefined;
      return {
        id: item.id,
        name: item.name,
        description: typeof item.description === 'string' && item.description.trim().length > 0
          ? item.description.trim()
          : null,
        type: item.type,
        source: item.source,
        provider: item.provider,
        sourceId: item.sourceId,
        allergens: normalizeStringList(item.allergens),
        allergenTagIds: normalizeStringList(item.allergenTagIds ?? item.allergens),
        dietaryTagIds: normalizeStringList(item.dietaryTagIds ?? item.dietLabels),
        dietLabels: normalizeStringList(item.dietLabels ?? item.dietaryTagIds),
        intoleranceTagIds: normalizeStringList(item.intoleranceTagIds ?? item.intoleranceTags),
        intoleranceTags: normalizeStringList(item.intoleranceTags ?? item.intoleranceTagIds),
        tagClassificationStatus:
          item.tagClassificationStatus === 'not_cached'
          || item.tagClassificationStatus === 'queued'
          || item.tagClassificationStatus === 'processing'
          || item.tagClassificationStatus === 'completed'
          || item.tagClassificationStatus === 'failed'
            ? item.tagClassificationStatus
            : undefined,
        tagClassificationSource:
          item.tagClassificationSource === 'preview'
          || item.tagClassificationSource === 'ai'
          || item.tagClassificationSource === 'none'
            ? item.tagClassificationSource
            : undefined,
        tagClassificationUpdatedAt:
          typeof item.tagClassificationUpdatedAt === 'string' ? item.tagClassificationUpdatedAt : null,
        isVegan: typeof item.isVegan === 'boolean' ? item.isVegan : undefined,
        isVegetarian: typeof item.isVegetarian === 'boolean' ? item.isVegetarian : undefined,
        isGlutenFree: typeof item.isGlutenFree === 'boolean' ? item.isGlutenFree : undefined,
        isDairyFree: typeof item.isDairyFree === 'boolean' ? item.isDairyFree : undefined,
        ...(ingredientServingMetadata?.possibleUnits.length
          ? { possibleUnits: ingredientServingMetadata.possibleUnits }
          : {}),
        ...(ingredientServingMetadata?.servingOverrides?.length
          ? { servingOverrides: ingredientServingMetadata.servingOverrides }
          : {}),
        ...(ingredientServingMetadata?.defaultServingId
          ? { defaultServingId: ingredientServingMetadata.defaultServingId }
          : {}),
        ...(ingredientServingMetadata?.defaultServingGrams != null
          ? { defaultServingGrams: ingredientServingMetadata.defaultServingGrams }
          : {}),
        calories: toOptionalNumber(nutrition?.calories),
        protein: toOptionalNumber(nutrition?.protein),
        carbs: toOptionalNumber(nutrition?.carbohydrate),
        fat: toOptionalNumber(nutrition?.total_fat),
        clinicalWarnings: Array.isArray(item.clinicalWarnings)
          ? item.clinicalWarnings.flatMap((warning) => {
              if (!warning || typeof warning !== 'object' || Array.isArray(warning)) return [];
              const record = warning as Record<string, unknown>;
              const message = typeof record.message === 'string' ? record.message.trim() : '';
              if (!message) return [];
              return [{
                ruleId: String(record.ruleId ?? 'clinical-warning'),
                severity: record.severity === 'blocking' ? 'blocking' as const : 'warning' as const,
                condition: String(record.condition ?? 'Clinical condition'),
                trigger: String(record.trigger ?? 'clinical review'),
                message,
              }];
            })
          : [],
      };
    });
/** Normalize recipe portion / serving id for manual update-servings API (matches MealSlotCard). */
const parseRecipeServingId = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 ? normalized : undefined;
};

const isIngredientRow = (
  row: Pick<ExpandedFoodRow, 'itemType' | 'ingredientId'>,
): boolean => row.itemType === 'ingredient' || Boolean(row.ingredientId?.trim());

const numberFromUnknown = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (value && typeof value === 'object' && 'quantity' in value) {
    const parsed = Number((value as { quantity?: unknown }).quantity);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const normalizeItemType = (value: unknown): 'recipe' | 'ingredient' => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'ingredient' ? 'ingredient' : 'recipe';
};

const resolveRowId = (
  mealName: string,
  index: number,
  itemId: string | undefined,
  itemName: string,
): string => {
  const normalizedItemId = String(itemId ?? '').trim();
  if (normalizedItemId) return `${mealName}-${normalizedItemId}`;
  const normalizedItemName = String(itemName ?? '').trim().toLowerCase().replace(/\s+/g, '-');
  return `${mealName}-${normalizedItemName || 'row'}-${index}`;
};

const nutritionValue = (nutrition: unknown, ...keys: string[]): number => {
  if (!nutrition || typeof nutrition !== 'object') return 0;
  const source = nutrition as Record<string, unknown>;
  const macroSource =
    source.macros && typeof source.macros === 'object'
      ? (source.macros as Record<string, unknown>)
      : source;
  const hasFraction = (value: number): boolean =>
    Math.abs(value - Math.trunc(value)) > Number.EPSILON;

  for (const key of keys) {
    const sourceResolved = numberFromUnknown(source[key]);
    const macroResolved = numberFromUnknown(macroSource[key]);

    // If both exist, prefer the non-rounded variant (fractional value) when available.
    if (sourceResolved !== 0 && macroResolved !== 0) {
      if (hasFraction(sourceResolved) && !hasFraction(macroResolved)) return sourceResolved;
      if (!hasFraction(sourceResolved) && hasFraction(macroResolved)) return macroResolved;
      return sourceResolved;
    }
    if (sourceResolved !== 0) return sourceResolved;
    if (macroResolved !== 0) return macroResolved;
  }
  return 0;
};

const cleanMealName = (value: string): string =>
  value
    .replace(/_\d+$/i, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const toTitleCase = (value: string): string =>
  value
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

const resolveMealDisplayName = (value: string, fallbackIndex: number): string => {
  const cleaned = cleanMealName(value);
  if (!cleaned) return `Meal ${fallbackIndex + 1}`;
  return toTitleCase(cleaned);
};

const resolveMealTime = (mealName: string): string => {
  const normalized = cleanMealName(mealName).toLowerCase();
  return defaultMealTimesByName[normalized] || '07:35 AM';
};

const formatMealSlotTimeForDisplay = (raw: string): string => {
  const t = String(raw ?? '').trim();
  if (!t) return '';
  if (/\b(am|pm)\b/i.test(t)) return t;
  for (const pattern of ['HH:mm:ss', 'H:mm:ss', 'HH:mm', 'H:mm'] as const) {
    const d = parse(t, pattern, MEAL_TIME_REF_DATE);
    if (isValid(d)) return format(d, 'hh:mm a');
  }
  return t;
};

/** Normalizes display or API time to `HH:mm` for meal-slot APIs and `<input type="time" />`. */
const mealDisplayTimeToApiHHMM = (display: string): string => {
  const t = String(display ?? '').trim();
  if (!t) return '07:35';
  const m24 = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (m24) {
    const h = Math.min(23, Math.max(0, parseInt(m24[1], 10)));
    const min = m24[2].padStart(2, '0');
    return `${String(h).padStart(2, '0')}:${min}`;
  }
  for (const pattern of ['h:mm a', 'hh:mm a', 'h:mm:ss a', 'hh:mm:ss a'] as const) {
    const d = parse(t, pattern, MEAL_TIME_REF_DATE);
    if (isValid(d)) return format(d, 'HH:mm');
  }
  return '07:35';
};

const resolveFinalizedMealType = (
  mealName: string,
): 'breakfast' | 'lunch' | 'dinner' | 'snack' => {
  const normalized = cleanMealName(mealName).toLowerCase();
  if (normalized.includes('dinner')) return 'dinner';
  if (normalized.includes('lunch')) return 'lunch';
  if (normalized.includes('snack')) return 'snack';
  if (normalized.includes('breakfast')) return 'breakfast';
  return 'snack';
};

const normalizeAIDraftIngredientIdentifier = (
  provider: 'spoonacular' | 'internal',
  rawId: string,
): string => {
  const cleaned = String(rawId ?? '').trim();
  if (!cleaned) return '';
  if (provider !== 'spoonacular') return cleaned;
  if (cleaned.startsWith('spoonacular_')) return cleaned;
  return /^\d+$/.test(cleaned) ? `spoonacular_${cleaned}` : cleaned;
};

const formatNutritionWithoutRoundOff = (value: number): string => {
  if (!Number.isFinite(value)) return '0';
  const normalized = Math.max(0, value);
  return roundNutritionDisplay(normalized);
};

const formatNumber = (value: number): string =>
  formatNutritionWithoutRoundOff(value);

const formatCalories = (value: number): string =>
  formatNutritionWithoutRoundOff(value);

/** Rows that participate in recipe/ingredient selection checkboxes (named recipe or ingredient). */
export function buildAddingItemRowKey(mealName: string, rowSearchId?: string): string {
  const meal = String(mealName ?? '').trim();
  const rowId = String(rowSearchId ?? '').trim();
  return rowId ? `${meal}::${rowId}` : `${meal}::__meal__`;
}

export type EmptyRowSelectionContext = {
  rowIndex: number;
  rowSearchId: string;
  type: 'food' | 'ingredient';
};

export function resolveEmptyRowSelectionContext(
  mealName: string,
  rows: ExpandedFoodRow[],
  preferredType: 'food' | 'ingredient' = 'food',
): EmptyRowSelectionContext | null {
  const findForType = (type: 'food' | 'ingredient'): EmptyRowSelectionContext | null => {
    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      if (String(row.item ?? '').trim()) continue;
      const rowType = row.itemType === 'ingredient' ? 'ingredient' : 'food';
      if (rowType !== type) continue;
      const rowSearchId = row.rowId ?? resolveRowId(mealName, index, row.itemId, row.item);
      return { rowIndex: index, rowSearchId, type: rowType };
    }
    return null;
  };

  return findForType(preferredType) ?? findForType(preferredType === 'food' ? 'ingredient' : 'food');
}

export function isPickableNutritionRow(row: ExpandedFoodRow): boolean {
  return (
    (row.itemType === 'recipe' || row.itemType === 'ingredient')
    && String(row.item ?? '').trim().length > 0
  );
}

const rowMacroTotals = (rows: ExpandedFoodRow[]): MacroTotals =>
  rows.reduce(
    (acc, row) => {
      acc.calories += numberFromUnknown(row.kcal);
      acc.protein += numberFromUnknown(row.protein);
      acc.carbs += numberFromUnknown(row.carbs);
      acc.fat += numberFromUnknown(row.fat);
      return acc;
    },
    { calories: 0, protein: 0, carbs: 0, fat: 0 } as MacroTotals,
  );

const toEditableNumber = (value: number): string =>
  formatNutritionWithoutRoundOff(value);

const clampPercent = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
};

function nutritionPercentOfTarget(value: number, target: number): number {
  if (!target || target <= 0) return 0;
  const raw = (value / target) * 100;
  if (!Number.isFinite(raw)) return 0;
  return Math.max(0, raw);
}

export type ManualNutritionDrvContext = {
  drvRequirements: unknown;
  primaryTypes: DrvPrimaryType[] | undefined;
  clientGoals?: unknown;
  eerKcal?: number;
  /** When true, use meal-plan response macro targets ahead of client goal macro values. */
  preferMealPlanMacroTargets?: boolean;
  /**
   * Meal plan `targetDaily` (calories + macro range endpoints). When present, calories come
   * from the plan while exact clientGoals macro grams still win over range endpoints.
   */
  mealPlanDailyMacroTargets?: { calories: number; protein: number; carbs: number; fat: number };
};

type FatBreakdownTotals = {
  mufa?: number | null;
  pufa?: number | null;
  saturatedFat?: number | null;
  transFat?: number | null;
};

const extractClientGoalMacroValue = (
  clientGoals: unknown,
  key: 'protein' | 'carbohydrates' | 'totalFat',
): number | undefined => {
  if (!clientGoals || typeof clientGoals !== 'object') return undefined;
  const source = clientGoals as Record<string, unknown>;
  const raw = source[key];
  if (!raw || typeof raw !== 'object') return undefined;
  const goal = raw as {
    value?: unknown;
    grams?: { value?: unknown } | null;
    kcal?: { value?: unknown } | null;
  };
  return (
    toOptionalNumber(goal.grams?.value) ??
    toOptionalNumber(goal.value) ??
    toOptionalNumber(goal.kcal?.value)
  );
};

const extractClientGoalDietaryFibreValue = (clientGoals: unknown): number | undefined => {
  if (!clientGoals || typeof clientGoals !== 'object') return undefined;
  const raw = (clientGoals as Record<string, unknown>).dietaryFibre;
  if (!raw || typeof raw !== 'object') return undefined;
  const goal = raw as { value?: unknown; grams?: { value?: unknown } | null };
  return toOptionalNumber(goal.grams?.value) ?? toOptionalNumber(goal.value);
};

const normalizeNutrientId = (value: unknown): string =>
  typeof value === 'string' ? value.replace(/[_\s-]/g, '').toLowerCase() : '';

const extractDrvMacroRdaThenAi = (
  drvRequirements: unknown,
  nutrientIdCandidates: string[],
): number | undefined => {
  if (!drvRequirements || typeof drvRequirements !== 'object') return undefined;
  const root = drvRequirements as Record<string, unknown>;
  const resolvedRoot =
    (root.drvRequirements && typeof root.drvRequirements === 'object'
      ? (root.drvRequirements as Record<string, unknown>)
      : root);
  const macros = resolvedRoot.macronutrients as Record<string, unknown[]> | undefined;
  if (!macros) return undefined;

  const rows = [
    ...(macros.proteins ?? []),
    ...(macros.carbohydrates ?? []),
    ...(macros.lipids ?? []),
  ];

  const normalizedCandidates = nutrientIdCandidates.map((candidate) => normalizeNutrientId(candidate));
  const matched = rows.find((item) => {
    if (!item || typeof item !== 'object') return false;
    const nutrientId = normalizeNutrientId((item as { nutrientId?: unknown }).nutrientId);
    return normalizedCandidates.includes(nutrientId);
  }) as { rda?: unknown; ai?: unknown } | undefined;

  return toOptionalNumber(matched?.rda) ?? toOptionalNumber(matched?.ai);
};

function resolveDrvTarget(
  drv: ManualNutritionDrvContext | null | undefined,
  nutrientIdCandidates: string[],
  fallback: number,
): number {
  if (!drv?.drvRequirements) return fallback;
  return resolveDrvRecommendedValue(drv.drvRequirements, drv.primaryTypes, nutrientIdCandidates, fallback);
}

function resolveDrvTargetPrimaryOnly(
  drv: ManualNutritionDrvContext | null | undefined,
  nutrientIdCandidates: string[],
  fallback: number,
): number {
  if (!drv?.drvRequirements) return fallback;
  return resolveDrvPrimaryTypeValueOnly(drv.drvRequirements, drv.primaryTypes, nutrientIdCandidates, fallback);
}

/** Aligns manual analysis targets with client DRV + `/drv/primary-types` (same rules as client NutritionReportTable). */
export function resolveManualNutritionTargets(
  macroTargets: { calories: number; protein: number; carbs: number; fat: number },
  drv: ManualNutritionDrvContext | null | undefined,
): {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  calcium: number;
  iron: number;
  vitaminD: number;
} {
  const planMacros = drv?.mealPlanDailyMacroTargets;
  if (
    planMacros
    && typeof planMacros.calories === 'number'
    && Number.isFinite(planMacros.calories)
    && planMacros.calories > 0
  ) {
    const calories = Math.max(1, planMacros.calories);
    const protein = Math.max(
      1,
      drv.preferMealPlanMacroTargets
        ? planMacros.protein
        : extractClientGoalMacroValue(drv?.clientGoals, 'protein') ?? planMacros.protein,
    );
    const carbs = Math.max(
      1,
      drv.preferMealPlanMacroTargets
        ? planMacros.carbs
        : extractClientGoalMacroValue(drv?.clientGoals, 'carbohydrates') ?? planMacros.carbs,
    );
    const fat = Math.max(
      1,
      drv.preferMealPlanMacroTargets
        ? planMacros.fat
        : extractClientGoalMacroValue(drv?.clientGoals, 'totalFat') ?? planMacros.fat,
    );
    const drvFiber = resolveDrvTargetPrimaryOnly(drv, ['dietary_fibre', 'dietary_fiber', 'fiber'], 35);
    const fiber = Math.max(1, extractClientGoalDietaryFibreValue(drv?.clientGoals) ?? drvFiber);
    const calcium = Math.max(1, resolveDrvTargetPrimaryOnly(drv, ['calcium'], 700));
    const iron = Math.max(1, resolveDrvTargetPrimaryOnly(drv, ['iron'], 14.8));
    const vitaminD = Math.max(1, resolveDrvTargetPrimaryOnly(drv, ['vitamin_d'], 10));
    return { calories, protein, carbs, fat, fiber, calcium, iron, vitaminD };
  }

  const caloriesFromEer =
    typeof drv?.eerKcal === 'number' && Number.isFinite(drv.eerKcal) && drv.eerKcal > 0
      ? drv.eerKcal
      : undefined;
  const calories = Math.max(
    1,
    caloriesFromEer
      ?? resolveDrvTarget(drv, ['energy'], Math.max(1, macroTargets.calories || 2000)),
  );

  const drvProtein = extractDrvMacroRdaThenAi(drv?.drvRequirements, ['protein'])
    ?? resolveDrvTarget(drv, ['protein'], Math.max(1, macroTargets.protein || 75));
  const drvCarbs = extractDrvMacroRdaThenAi(drv?.drvRequirements, ['carbohydrate', 'carbohydrates'])
    ?? resolveDrvTarget(drv, ['carbohydrate', 'carbohydrates'], Math.max(1, macroTargets.carbs || 230));
  const drvFat = extractDrvMacroRdaThenAi(drv?.drvRequirements, ['total_fat', 'fat', 'lipid'])
    ?? resolveDrvTarget(drv, ['total_fat', 'fat', 'lipid'], Math.max(1, macroTargets.fat || 85));

  // Priority: clientGoals macros -> DRV macronutrient (RDA -> AI) -> plan/day macro target fallback.
  const protein = Math.max(
    1,
    extractClientGoalMacroValue(drv?.clientGoals, 'protein') ?? drvProtein,
  );
  const carbs = Math.max(
    1,
    extractClientGoalMacroValue(drv?.clientGoals, 'carbohydrates') ?? drvCarbs,
  );
  const fat = Math.max(
    1,
    extractClientGoalMacroValue(drv?.clientGoals, 'totalFat') ?? drvFat,
  );
  const drvFiber = resolveDrvTargetPrimaryOnly(drv, ['dietary_fibre', 'dietary_fiber', 'fiber'], 35);
  // Priority: clientGoals.dietaryFibre (e.g. { value: 33.3, unit: "g/day" }) -> DRV primary-type field -> fallback.
  const fiber = Math.max(1, extractClientGoalDietaryFibreValue(drv?.clientGoals) ?? drvFiber);
  const calcium = Math.max(1, resolveDrvTargetPrimaryOnly(drv, ['calcium'], 700));
  const iron = resolveDrvTargetPrimaryOnly(drv, ['iron'], 14.8);
  const vitaminD = resolveDrvTargetPrimaryOnly(drv, ['vitamin_d'], 10);
  return { calories, protein, carbs, fat, fiber, calcium, iron, vitaminD };
}

/** Meals for analysis snapshot: prefer plan meal order; if empty, derive from rows; append any slot keys missing from meal cards. */
function deriveMealsForAnalysisSnapshot(
  mealCards: Array<{ name: string; totals: MacroTotals }>,
  rowsByMeal: Record<string, ExpandedFoodRow[]>,
): Array<{ name: string; totals: MacroTotals }> {
  const rowKeys = Object.keys(rowsByMeal);
  if (mealCards.length === 0) {
    return rowKeys
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({
        name,
        totals: rowMacroTotals(rowsByMeal[name] ?? []),
      }));
  }
  const seen = new Set(mealCards.map((m) => m.name));
  const extras = rowKeys
    .filter((k) => !seen.has(k))
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({
      name,
      totals: rowMacroTotals(rowsByMeal[name] ?? []),
    }));
  return extras.length > 0 ? [...mealCards, ...extras] : mealCards;
}

/** Same Suggested RDA palette as meal-plan sidebar + nutrient report (`getNutritionTrafficLightColor`). */
export function getTrafficLightColor(percent: number, isLowerBoundOnly = false): string {
  return getNutritionTrafficLightColor(percent, isLowerBoundOnly);
}

function resolveDrvUpperLimit(
  drv: ManualNutritionDrvContext | null | undefined,
  nutrientIdCandidates: string[],
): number | null {
  const requirement = findDrvRequirementByNutrientIds(drv?.drvRequirements, nutrientIdCandidates);
  if (!requirement) return null;
  const ul = toOptionalNumber(requirement.ul);
  return ul != null && ul > 0 ? ul : null;
}

/**
 * Calcium / iron / vitamin D bars — same rules as nutrient report micros status:
 * met at/above RDA until upper limit (UL); toxic at/above UL; deficient below RDA.
 */
export function getMicronutrientTrafficLightColor(
  intake: number,
  target: number,
  upperLimit?: number | null,
): string {
  if (!Number.isFinite(intake) || target <= 0) return '#D0D5DD';

  if (upperLimit != null && upperLimit > 0 && intake >= upperLimit) {
    return '#ef4444';
  }

  if (intake >= target) {
    return '#10b981';
  }

  const percent = (intake / target) * 100;
  if (percent >= 90) return '#10b981';
  if (percent >= 80) return '#f59e0b';
  return '#ef4444';
}

function buildManualNutritionAnalysisSnapshot(
  dayLabel: string,
  mealCards: Array<{ name: string; totals: MacroTotals }>,
  totalIntake: MacroTotals,
  macroTargets: { calories: number; protein: number; carbs: number; fat: number },
  rowsByMeal: Record<string, ExpandedFoodRow[]>,
  drvContext?: ManualNutritionDrvContext | null,
  apiTopCalorieContributors?: ContributorRow[] | null,
  apiNutrientTotals?: { fiber?: number; calcium?: number; iron?: number; vitaminD?: number } | null,
  apiFatBreakdownTotals?: FatBreakdownTotals | null,
): DayAnalysisSnapshot {
  const mealsForSnapshot = deriveMealsForAnalysisSnapshot(mealCards, rowsByMeal);

  const resolved = resolveManualNutritionTargets(macroTargets, drvContext ?? null);
  const caloriesTarget = resolved.calories;
  const proteinTarget = resolved.protein;
  const carbsTarget = resolved.carbs;
  const fatTargetRda = resolved.fat;
  const fatLimit = (drvContext?.clientGoals as any)?.clinicalRules?.resolvedTargets?.limits?.fat;
  const fatStatus = fatLimit ? clinicalLimitStatus({ limit: fatLimit, intake: totalIntake.fat }) : null;
  const fatColor = fatStatus ? suggestedRdaToneStyles[fatStatus.tone].bar : getTrafficLightColor(nutritionPercentOfTarget(totalIntake.fat, fatTargetRda));
  const fiberTarget = resolved.fiber;
  const fibreLimit = (drvContext?.clientGoals as any)?.clinicalRules?.resolvedTargets?.limits?.fibre;
  const fibreStatus = fibreLimit ? clinicalLimitStatus({ limit: fibreLimit, intake: Math.max(0, numberFromUnknown(apiNutrientTotals?.fiber)) }) : null;

  const totalCalories = Math.max(0, totalIntake.calories);
  const totalProtein = Math.max(0, totalIntake.protein);
  const totalCarbs = Math.max(0, totalIntake.carbs);
  const totalFat = Math.max(0, totalIntake.fat);
  const totalFiber = Math.max(0, numberFromUnknown(apiNutrientTotals?.fiber));
  const totalCalcium = Math.max(0, numberFromUnknown(apiNutrientTotals?.calcium));
  const totalIron = Math.max(0, numberFromUnknown(apiNutrientTotals?.iron));
  const totalVitaminD = Math.max(0, numberFromUnknown(apiNutrientTotals?.vitaminD));

  const macroCalorieSum = Math.max(1, totalProtein * 4 + totalCarbs * 4 + totalFat * 9);
  const macroSplit = {
    carbs: clampPercent((totalCarbs * 4 * 100) / macroCalorieSum),
    protein: clampPercent((totalProtein * 4 * 100) / macroCalorieSum),
    fat: clampPercent((totalFat * 9 * 100) / macroCalorieSum),
  };

  const pctEnergy = nutritionPercentOfTarget(totalCalories, caloriesTarget);
  const pctProtein = nutritionPercentOfTarget(totalProtein, proteinTarget);
  const pctCarbs = nutritionPercentOfTarget(totalCarbs, carbsTarget);
  const pctFat = nutritionPercentOfTarget(totalFat, fatTargetRda);
  const pctFiber = nutritionPercentOfTarget(totalFiber, fiberTarget);
  const pctCalcium = nutritionPercentOfTarget(totalCalcium, resolved.calcium);
  const pctIron = nutritionPercentOfTarget(totalIron, resolved.iron);
  const pctVitD = nutritionPercentOfTarget(totalVitaminD, resolved.vitaminD);

  const calciumUl = resolveDrvUpperLimit(drvContext, ['calcium']);
  const ironUl = resolveDrvUpperLimit(drvContext, ['iron']);
  const vitaminDUl = resolveDrvUpperLimit(drvContext, ['vitamin_d', 'vitamin_d3']);

  const nutrientVsRda = [
    { key: 'energy', label: 'Energy', percent: pctEnergy, color: getTrafficLightColor(pctEnergy, false) },
    { key: 'protein', label: 'Protein', percent: pctProtein, color: getTrafficLightColor(pctProtein, false) },
    { key: 'carbs', label: 'Carbs', percent: pctCarbs, color: getTrafficLightColor(pctCarbs, false) },
    { key: 'fat', label: 'Fat', percent: pctFat, color: fatColor },
    { key: 'fibre', label: 'Fibre', percent: pctFiber, color: fibreStatus ? suggestedRdaToneStyles[fibreStatus.tone].bar : getTrafficLightColor(pctFiber, true) },
    {
      key: 'calcium',
      label: 'Calcium',
      percent: pctCalcium,
      color: getMicronutrientTrafficLightColor(totalCalcium, resolved.calcium, calciumUl),
    },
    {
      key: 'iron',
      label: 'Iron',
      percent: pctIron,
      color: getMicronutrientTrafficLightColor(totalIron, resolved.iron, ironUl),
    },
    {
      key: 'vitd',
      label: 'Vit D',
      percent: pctVitD,
      color: getMicronutrientTrafficLightColor(totalVitaminD, resolved.vitaminD, vitaminDUl),
    },
  ];

  const mealCalories = mealsForSnapshot.map((meal, index) => {
    const calories = Math.max(0, meal.totals.calories);
    const percent = totalCalories > 0 ? clampPercent((calories * 100) / totalCalories) : 0;
    return {
      key: `${meal.name}-${index}`,
      label: meal.name,
      calories,
      percent,
      color: MANUAL_ANALYSIS_MEAL_COLORS[index % MANUAL_ANALYSIS_MEAL_COLORS.length],
    };
  });

  const resolveFatBreakdownValue = (value: unknown): number | null => {
    const parsed = toOptionalNumber(value);
    if (parsed == null) return null;
    return Math.max(0, parsed);
  };

  const fatBreakdownValues = [
    {
      key: 'mono',
      label: 'Monounsaturated',
      value: resolveFatBreakdownValue(apiFatBreakdownTotals?.mufa),
      color: '#2fb0d4',
    },
    {
      key: 'poly',
      label: 'Polyunsaturated',
      value: resolveFatBreakdownValue(apiFatBreakdownTotals?.pufa),
      color: '#d5dbe3',
    },
    {
      key: 'trans',
      label: 'Trans',
      value: resolveFatBreakdownValue(apiFatBreakdownTotals?.transFat),
      color: '#8dd9f2',
    },
    {
      key: 'saturated',
      label: 'Saturated',
      value: resolveFatBreakdownValue(apiFatBreakdownTotals?.saturatedFat),
      color: '#2cb3b8',
    },
  ].filter(
    (
      item,
    ): item is { key: string; label: string; value: number; color: string } => item.value != null,
  );

  const fatBreakdownTotal = fatBreakdownValues.reduce((sum, item) => sum + item.value, 0);
  const fatBreakdown = fatBreakdownValues.map((item) => ({
    ...item,
    percent: fatBreakdownTotal > 0 ? clampPercent((item.value * 100) / fatBreakdownTotal) : 0,
  }));

  const mealOrderIndex = new Map<string, number>();
  mealsForSnapshot.forEach((m, i) => mealOrderIndex.set(m.name, i));

  const topContributorsFromRows = Object.entries(rowsByMeal)
    .flatMap(([mealName, rows]) => {
      const mealIndex = mealOrderIndex.get(mealName) ?? 0;
      const mealColor = MANUAL_ANALYSIS_MEAL_COLORS[mealIndex % MANUAL_ANALYSIS_MEAL_COLORS.length];
      return rows.map((row, recipeIndex) => {
        const kcal = numberFromUnknown(row.kcal);
        return {
          key: `${mealName}-${recipeIndex}-${row.rowId ?? row.item}`,
          title: row.item,
          detailLabel: [String(row.qty ?? '').trim(), String(row.unit ?? '').trim()].filter(Boolean).join(' ') || '—',
          mealLabel: mealName,
          mealColor,
          calories: Math.max(0, kcal),
          percent: totalCalories > 0 ? clampPercent((kcal * 100) / totalCalories) : 0,
          color: mealColor,
        };
      });
    })
    .filter((row) => row.title.trim().length > 0 && row.calories > 0)
    .sort((a, b) => b.calories - a.calories)
    .slice(0, 10);

  const topContributors =
    apiTopCalorieContributors != null && apiTopCalorieContributors.length > 0
      ? [...apiTopCalorieContributors]
          .map((row) => ({
            ...row,
            percent: totalCalories > 0 ? clampPercent((row.calories * 100) / totalCalories) : 0,
          }))
          .sort((a, b) => b.calories - a.calories)
          .slice(0, 10)
      : topContributorsFromRows;

  return {
    dayLabel,
    totalCalories,
    totalMeals: mealsForSnapshot.length,
    macroSplit,
    nutrientVsRda,
    mealCalories,
    macroProgressCards: [
      {
        key: 'carbs',
        label: 'Carbohydrates',
        value: totalCarbs,
        target: carbsTarget,
        color: getTrafficLightColor(nutritionPercentOfTarget(totalCarbs, carbsTarget), false),
      },
      {
        key: 'protein',
        label: 'Protein',
        value: totalProtein,
        target: proteinTarget,
        color: getTrafficLightColor(nutritionPercentOfTarget(totalProtein, proteinTarget), false),
      },
      {
        key: 'total-fat',
        label: 'Total Fat',
        value: totalFat,
        target: fatTargetRda,
        color: fatColor,
      },
    ],
    totalFat,
    fatTarget: fatTargetRda,
    fatBreakdown,
    topContributors,
  };
}

const withCurrentOption = (currentValue: string, options: string[]) =>
  options.includes(currentValue) ? options : [currentValue, ...options];

const parseRecipeServingSizesFromRecord = (
  recipeRecord: Record<string, unknown>,
  itemType: 'recipe' | 'ingredient',
): RecipeServingSizeOption[] | undefined => {
  if (itemType !== 'recipe') return undefined;
  const raw = recipeRecord.servingSizes;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const parsed: RecipeServingSizeOption[] = [];
  for (const entry of raw) {
    if (entry == null || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    const servingId = String(e.servingId ?? '').trim();
    if (!servingId.length) continue;
    const gramsN = Number(e.grams);
    parsed.push({
      servingId,
      grams: Number.isFinite(gramsN) ? gramsN : undefined,
      label: typeof e.label === 'string' ? e.label : null,
      isDefault: e.isDefault === true,
    });
  }
  return parsed.length > 0 ? parsed : undefined;
};

const resolveInitialRecipeServingId = (
  recipeRecord: Record<string, unknown>,
  sizes: RecipeServingSizeOption[] | undefined,
  legacyServing: string,
): string => {
  const selected = typeof recipeRecord.selectedServingId === 'string' ? recipeRecord.selectedServingId.trim() : '';
  const def = typeof recipeRecord.defaultServingId === 'string' ? recipeRecord.defaultServingId.trim() : '';
  const idSet = new Set((sizes ?? []).map((s) => s.servingId));
  if (selected && idSet.has(selected)) return selected;
  const legacy = String(legacyServing ?? '').trim();
  if (legacy && idSet.has(legacy)) return legacy;
  if (def && idSet.has(def)) return def;
  const marked = sizes?.find((s) => s.isDefault);
  if (marked) return marked.servingId;
  if (sizes?.[0]) return sizes[0].servingId;
  if (selected) return selected;
  if (def) return def;
  return legacyServing;
};

const buildServingSelectOptions = (
  row: ExpandedFoodRow,
): { value: string; label: string }[] => {
  if (row.itemType === 'recipe') {
    if (row.recipeServingSizes && row.recipeServingSizes.length > 0) {
      const menu = row.recipeServingSizes.map((s) => {
        const gramsSuffix = s.grams != null && Number.isFinite(s.grams) ? ` (${s.grams}g)` : '';
        const baseLabel = formatUnderscoreDisplayLabel(s.label && s.label.trim().length > 0 ? s.label : s.servingId);
        return { value: s.servingId, label: `${baseLabel}${gramsSuffix}` };
      });
      const ids = new Set(menu.map((m) => m.value));
      if (row.serving && !ids.has(row.serving)) {
        return [{ value: row.serving, label: formatUnderscoreDisplayLabel(row.serving) }, ...menu];
      }
      return menu;
    }
    const s = String(row.serving ?? '').trim();
    return s ? [{ value: s, label: formatUnderscoreDisplayLabel(s) }] : [{ value: '', label: '—' }];
  }
  if (row.itemType === 'ingredient') {
    const s = String(row.serving ?? '').trim();
    return s ? [{ value: s, label: formatUnderscoreDisplayLabel(s) }] : [{ value: '', label: '—' }];
  }
  const s = String(row.serving ?? '').trim();
  return s ? [{ value: s, label: s }] : [{ value: '', label: '—' }];
};

const buildRowUnitSelectOptions = (
  row: ExpandedFoodRow,
): { value: string; label: string }[] => {
  if (isIngredientRow(row)) {
    const currentUnit = String(row.unit ?? '').trim();
    const optionsFromApi = normalizeUnitOptions(row.possibleUnits);
    const gramsByUnit = new Map<string, number>();
    (row.servingOverrides ?? []).forEach((option) => {
      const normalizedUnit = String(option.servingId ?? '').trim().toLowerCase();
      if (!normalizedUnit.length) return;
      const grams = Number(option.grams);
      if (!Number.isFinite(grams)) return;
      gramsByUnit.set(normalizedUnit, grams);
    });
    const defaultServingUnit = String(row.defaultServingId ?? '').trim().toLowerCase();
    const defaultServingGrams = Number(row.defaultServingGrams);
    if (
      defaultServingUnit.length > 0
      && Number.isFinite(defaultServingGrams)
      && !gramsByUnit.has(defaultServingUnit)
    ) {
      gramsByUnit.set(defaultServingUnit, defaultServingGrams);
    }

    const toUnitLabel = (value: string): string => {
      if (!value) return '—';
      const grams = gramsByUnit.get(value.toLowerCase());
      if (!Number.isFinite(grams)) return value;
      return `${value} (${formatNutritionWithoutRoundOff(grams)}g)`;
    };

    if (optionsFromApi.length > 0) {
      const hasCurrentUnit = currentUnit.length > 0
        && optionsFromApi.some((value) => value.toLowerCase() === currentUnit.toLowerCase());
      const unitOptions = hasCurrentUnit
        ? optionsFromApi
        : currentUnit.length > 0
          ? [currentUnit, ...optionsFromApi]
          : optionsFromApi;
      return unitOptions.map((value) => ({ value, label: toUnitLabel(value) }));
    }
    if (currentUnit.length > 0) {
      return [{ value: currentUnit, label: toUnitLabel(currentUnit) }];
    }
    return [{ value: '', label: '—' }];
  }

  return buildServingSelectOptions(row);
};

const ensureDefaultEmptyRowsForMeal = (
  apiMealName: string,
  rows: ExpandedFoodRow[],
): ExpandedFoodRow[] => {
  const hasEmptySearchRow = rows.some((row) => !String(row.item ?? '').trim());

  if (hasEmptySearchRow) {
    return [...rows];
  }

  const nextRows = [...rows];
  nextRows.push({
    rowId: resolveRowId(apiMealName, nextRows.length, undefined, ''),
    itemType: 'recipe',
    item: '',
    qty: 1,
    unit: '',
    serving: '',
    kcal: '',
    protein: '',
    carbs: '',
    fat: '',
  });

  return nextRows;
};

export {
  buildManualNutritionAnalysisSnapshot,
  buildRowUnitSelectOptions,
  buildServingSelectOptions,
  clampPercent,
  cleanMealName,
  ensureDefaultEmptyRowsForMeal,
  formatCalories,
  formatMealSlotTimeForDisplay,
  formatNumber,
  isIngredientRow,
  mapFoodSearchResults,
  mealDisplayTimeToApiHHMM,
  normalizeAIDraftIngredientIdentifier,
  normalizeItemType,
  resolveIngredientServingMetadataFromRecord,
  resolveIngredientUnitOptionsFromRecord,
  numberFromUnknown,
  nutritionValue,
  parseRecipeServingId,
  parseRecipeServingSizesFromRecord,
  resolveFinalizedMealType,
  resolveInitialRecipeServingId,
  resolveMealDisplayName,
  resolveMealTime,
  resolveRowId,
  rowMacroTotals,
  toEditableNumber,
};
export { roundNutritionDisplay } from '@/utils/nutrition-display-format';
